import React from "react";
// findDOMNode removed in React 19, using ref instead
import ReactPlayer from "react-player";
import screenfull from "screenfull";
import Mark from "../../../common/components/mark/Mark";
import { pad } from "../../../common/common_tools";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import img from "./../../../dist/images/broken_link.png";
import { convertHMStoSeconds } from "../../../common/common_tools";
import { TimerSharp } from "@mui/icons-material";
import { Button, InputGroup } from "react-bootstrap";
import i18n from "i18next";
import { isRecorderMode } from "../../../_visor/utils/recorderMode";
import { getYoutubeVideoId, isYoutubeUrl } from "../../../common/youtube_tools";
import { getMasterVolume, onMasterVolume } from "../../../_visor/utils/masterVolume";
import { registerSeek, unregisterSeek } from "../../../_visor/utils/recorderSeekRegistry";
import { sceneEndClosesScene } from "../../../_visor/chrome/chromeState";
import { getNextValidPage } from "../../../_visor/utils/visorNavigation";
import RecorderCanvasVideo from "../../../_visor/components/canvas-video/RecorderCanvasVideo";
import { sceneTimeBus } from "../../../common/scene_clock.es6";
import {
  createZoomCamera,
  videoLookGeometry,
  videoLookPlayerStyle,
  renderVideoLook,
  VIDEO_LOOK_WRAPPER_STYLE,
  hasInfiniteDuration,
  probeInfiniteDuration,
} from "./videoZoom";
/* eslint-disable react/prop-types */

const PROGRESS_INTERVAL_MS = 300;
// In-points are frames: half a frame tells a whole second, and an in-point already reached.
const HALF_FRAME_SEC = 1 / 60;
const isWholeSecond = (sec) => Math.abs(sec - Math.round(sec)) < HALF_FRAME_SEC;

function fmtTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.trunc(seconds / 60);
  const s = Math.trunc(seconds % 60);
  return m + ":" + pad(s);
}

export default class EnrichedPlayerPlugin extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      // Volume set by the author in the editor persists on plugin state and
      // becomes the learner's playback level here.
      volume: typeof props.state?.volume === "number" ? props.state.volume : 1,
      duration: 0,
      played: 0,
      seeking: false,
      fullscreen: false,
      controls: true,
      toBeTriggered: [],
      triggering: false,
      playedSeconds: 0,
      playing: false,
      playButtonClicked: false,
      firstEnded: false,
      comingFromMark: false,
      // Trimmed/split clips stay hidden until the element actually shows the
      // in-point — see _inPointGuardNeeded.
      atInPoint: !this._inPointGuardNeeded(props),
    };
    this._inPointTarget = null;
    this._inPointHandlers = null;
    // Suppresses onPause during timebar seek to prevent browser pause event
    // from canceling the resume after seekTo
    this._suppressNextPause = false;
    this._suppressPauseTimer = null;
    // rVFC progress tracking
    this._rVfcHandle = null;
    this._rVfcDisposed = false;
    this._rVfcLastReportTime = 0;
    // Load-failure safety timer
    this._loadFailureTimer = null;
    // YouTube ↔ buffer-gate coupling: whether THIS box's buffering currently
    // holds the gate closed, and whether the gate (closed by another box)
    // auto-paused this video — so only the gate's reopening resumes it.
    this._reportedStall = false;
    this._pausedByBufferGate = false;
    // YouTube drift-correction cooldown (hard seeks only — YT has no ±5% rate)
    this._lastDriftSeekMs = 0;
    // One-shot load() kick per url — see _maybeKickElementLoad.
    this._loadKickedFor = null;
    // YouTube preload prebuffer (muted play during the loading screen) —
    // see _maybeStartYtPrebuffer / the prebuffer branch in _ytWatchTick.
    this._ytPrebuffering = false;
    this._ytPrebufferDone = false;
    // True once YT has genuinely played (>0.5s) — its INITIAL boot must
    // never freeze the scene; only mid-play stalls may.
    this._ytEverPlayed = false;
    // Scene-end hold for timeline-driven YouTube — see _ytHeldAtSceneEnd.
    this._ytSceneEndHold = false;
  }

  // Set by the aidea-scene-ended broadcast for an autoPlay YouTube, released
  // when the playhead moves back (seek / replay / scene re-entry). The scene
  // clock keeps running past the end with its frame clamped there, and the
  // one-shot stop alone did not hold: YT's own PLAYING (a pause swallowed by
  // a seek in flight) re-legitimized playback, and drift correction then
  // hard-seeked the embed back to the frozen end frame every 4s — it played
  // on behind the CTA with stuttering audio. While held, only an explicit
  // learner action (playPause / replayVideo set state.playing first) starts
  // it, and drift correction stays off.
  _ytHeldAtSceneEnd() {
    return this._ytSceneEndHold === true && isYoutubeUrl(this.props.state.url);
  }

  // ── In-point guard (trimmed / split clips) ───────────────────────────
  // A <video> loads at t=0; only a later seek moves it to trimStart. A piece
  // mounting mid-scene therefore painted the SOURCE's first frame until that
  // seek landed. Two-part fix: seek at loadedmetadata (before any frame is
  // decoded) and keep the wrapper hidden until the displayed position is at
  // the in-point. Not needed for YouTube (no element) or recorder mode.
  _inPointGuardNeeded(props = this.props) {
    if (isRecorderMode()) return false;
    if (!props.state || isYoutubeUrl(props.state.url)) return false;
    return this._getTrimStartSec(props) > 0;
  }

  // Scene seconds → media seconds for this clip: offset by the play-window
  // start and the source in-point, clamped to the trimmed range. The scene
  // clock and the file's own time only coincide for an untrimmed clip that
  // starts at frame 0 — seeking the raw scene time into a split/moved piece
  // showed the wrong part of the source until the frame-synced controller
  // corrected it.
  _sceneSecToMediaSec(sceneSec, props = this.props) {
    const box = props.props?.parentBox || props.props?.boxes?.[props.props?.id];
    const tl = box?.timeline || {};
    const startFrame = typeof tl.startFrame === "number" ? tl.startFrame : 0;
    const autoPlay = props.state?.autoPlay === true;
    const playStartFrame =
      autoPlay && typeof tl.playStartFrame === "number" ? tl.playStartFrame : startFrame;
    const trimStartSec = this._getTrimStartSec(props);
    const trimEndSec = this._getTrimEndSec(props);
    let t = trimStartSec + Math.max(0, sceneSec - playStartFrame / 30);
    if (trimEndSec !== null) t = Math.min(t, trimEndSec);
    return t;
  }

  _ownSceneId() {
    const cv = this.props.props?.currentView;
    return Array.isArray(cv) ? cv[cv.length - 1] : cv;
  }

  // Live: this scene's committed frame; recorder (or before one): the prop.
  _frameNow(props = this.props) {
    if (!isRecorderMode() && sceneTimeBus.frameSceneId === this._ownSceneId()) {
      return sceneTimeBus.frame;
    }
    return props.props?.visorFrameTime || 0;
  }

  _onCommittedFrame() {
    if (sceneTimeBus.frameSceneId !== this._ownSceneId()) return;
    const prev = this._lastCommittedFrame;
    const frame = sceneTimeBus.frame;
    this._lastCommittedFrame = frame;
    if (prev == null || prev === frame) return;
    this._maybeStartDeferredYoutube(prev, frame);
    this._maybeStopMediaAtSceneEnd(prev, frame);
  }

  // Where the element should sit right now: the scene-clock position for
  // timeline-driven (autoPlay) media, the in-point for learner-driven media.
  _timelineTargetSec(props = this.props) {
    if (props.state?.autoPlay !== true) return this._getTrimStartSec(props);
    return this._sceneSecToMediaSec(this._frameNow(props) / 30, props);
  }

  _seekToTimelineTarget(el) {
    if (!el || el.seeking || this._getTrimStartSec() <= 0) return;
    const target = this._timelineTargetSec();
    if (Math.abs(el.currentTime - target) > 0.1) {
      try {
        el.currentTime = target;
      } catch (_) {}
    }
  }

  _checkInPoint(el) {
    if (this.state.atInPoint || !el) return;
    if (!el.seeking && el.currentTime >= this._getTrimStartSec() - 0.1) {
      this.setState({ atInPoint: true });
    }
  }

  _onElementLoadedMetadata = (e) => {
    // Hidden scenes stay lazy — their seek comes with the show transition.
    if (this.props.props.show) this._seekToTimelineTarget(e.currentTarget);
  };

  _detachInPointListeners() {
    if (this._inPointTarget && this._inPointHandlers) {
      Object.keys(this._inPointHandlers).forEach((ev) =>
        this._inPointTarget.removeEventListener(ev, this._inPointHandlers[ev])
      );
    }
    this._inPointTarget = null;
    this._inPointHandlers = null;
  }

  // Preload-screen prebuffer: while the ACTIVE scene is still loading, play
  // the YouTube muted so it gathers a buffer head like the file videos do —
  // the scene then opens with YT ready instead of freeze-cycling through its
  // boot. Completion (pause + rewind + unmute + registry "done") happens in
  // _ytWatchTick's prebuffer branch; the preload gate polls the registry.
  _maybeStartYtPrebuffer() {
    if (this._ytPrebufferDone || this._ytPrebuffering) return;
    if (!isYoutubeUrl(this.props.state.url)) return;
    if (!this.props.props.show) return;
    if (this.props.props.preloadCompleted && this.props.props.startButtonClicked) {
      // Scene already running — nothing to prebuffer, never block the gate.
      this._markYtPrebuffer("done");
      return;
    }
    if (!this.state.ready) return; // player still booting; retried each update
    this._ytPrebuffering = true;
    this._setDebugYtState("prebuffering");
    try {
      const internal =
        this.player && this.player.getInternalPlayer && this.player.getInternalPlayer();
      if (internal) {
        if (typeof internal.mute === "function") internal.mute();
        if (typeof internal.playVideo === "function") internal.playVideo();
      }
    } catch (_) {}
  }

  // A seek is a fresh start at a new position: its rebuffering must be
  // tolerated like the initial boot (never gate the scene) instead of
  // freeze-cycling — so the boot exception re-arms. The watch re-sets
  // _ytEverPlayed once it observes sustained playback at the new position;
  // stalls AFTER that gate the scene again as usual.
  _resetYtBootException() {
    if (!isYoutubeUrl(this.props.state.url)) return;
    this._ytEverPlayed = false;
    this._ytStartCancelled = false;
    if (this._ytWatch) {
      this._ytWatch.baseAt = null;
      this._ytWatch.baseT = null;
      this._ytWatch.idleT = null;
      this._ytWatch.idleAt = null;
      this._ytWatch.idlePlayMs = 0;
      this._ytWatch.tolerantSince = null;
    }
  }

  // The video's own time moving at its applied rate. A seek or a load moves it back, or ahead by
  // more than the wall time allows (one progress report of slack on each side).
  _isPlaybackStep(delta, wallMs) {
    if (!(delta > 0)) return false;
    return delta <= ((wallMs + 2 * PROGRESS_INTERVAL_MS) / 1000) * this._appliedYoutubeRate();
  }

  _markYtPrebuffer(status) {
    this._ytPrebufferDone = true;
    this._ytPrebuffering = false;
    if (typeof window !== "undefined") {
      const reg = (window.__visorYtPrebuffer = window.__visorYtPrebuffer || {});
      reg[this.props.state.url] = status;
    }
  }

  _finishYtPrebuffer() {
    // The scene may have started this box already: a pause here would stop it for good.
    const pp = this.props.props;
    const started = this.state.playing && pp.preloadCompleted && pp.startButtonClicked && pp.show;
    // Only a scene-driven video goes to the scene position; a manual one stays where it is.
    const sceneDriven = started && this.props.state.autoPlay === true;
    const startSec = sceneDriven ? this._sceneSecToMediaSec(this._frameNow() / 30) : 0;
    try {
      const internal =
        this.player && this.player.getInternalPlayer && this.player.getInternalPlayer();
      if (internal) {
        if (!started && typeof internal.pauseVideo === "function") internal.pauseVideo();
        if ((!started || sceneDriven) && typeof internal.seekTo === "function") {
          internal.seekTo(startSec, true);
        }
        if (typeof internal.unMute === "function") internal.unMute();
      }
    } catch (_) {}
    // Fresh slate for the real scene start: prebuffer playback must not leak
    // into the boot exception or the position/rate baselines.
    this._ytEverPlayed = false;
    if (this._ytWatch) {
      this._ytWatch.baseAt = null;
      this._ytWatch.baseT = null;
      this._ytWatch.idleT = null;
    }
    if (!started || sceneDriven) this.setState({ playedSeconds: startSec, played: 0 });
    this._setDebugYtState("prebuffered");
    this._markYtPrebuffer("done");
  }

  // An element mounted with preload="none" does NOT start fetching when the
  // attribute later flips to auto/metadata (browsers read the hint at load
  // time) — the scene-start wait then starves at rs0 forever. Kick load()
  // once when the blob decision resolves and the element is still empty.
  _maybeKickElementLoad() {
    const rawUrl = this.props.state.url;
    if (this._loadKickedFor === rawUrl) return;
    if (isYoutubeUrl(rawUrl)) return;
    if (this._videoPreloadAttr() === "none") return; // decision still pending
    if (this.state.playing || (this.state.playedSeconds || 0) > 0.1) {
      this._loadKickedFor = rawUrl;
      return;
    }
    const el = document.querySelector(`#box-${this.props.props.id} video`);
    if (!el) return;
    this._loadKickedFor = rawUrl;
    // NETWORK_LOADING(2) means a fetch is already running; rs>0 means data came.
    if (el.readyState === 0 && el.networkState !== 2) {
      try {
        el.load();
      } catch (_) {}
    }
  }

  // Single stall-report choke point — keeps this._reportedStall (=="this box
  // is the one holding the gate closed") in sync for BOTH media kinds, so the
  // gate coupling in componentDidUpdate can exempt the starving box itself.
  _reportStall(stalled) {
    if (typeof this.props.props.reportVideoStall !== "function") return;
    this._reportedStall = stalled;
    try {
      this.props.props.reportVideoStall(this.props.props.id, stalled);
    } catch (_) {}
  }

  _reportYoutubeStall(stalled) {
    if (!isYoutubeUrl(this.props.state.url)) return;
    // The BUFFERING event itself never gates the scene — it fires on every
    // start/seek and flaps in sub-250ms episodes (each attempt to gate on it
    // ended in stuck/cycling freezes). Genuine SUSTAINED starvation is
    // detected and reported by the rate watch instead (_ytWatchTick), which
    // carries the boot/seek tolerances. Only the overlay state is set here.
    this._setDebugYtState(stalled ? "buffering" : "playing");
  }

  // Pause the underlying player DIRECTLY — needed when the playing prop is
  // already false (no prop transition → ReactPlayer won't re-send pause) but
  // the real player is running anyway (unsolicited YT starts, swallowed
  // pause commands).
  _forcePauseInternal() {
    try {
      const internal =
        this.player && this.player.getInternalPlayer && this.player.getInternalPlayer();
      if (!internal) return;
      if (typeof internal.pauseVideo === "function") internal.pauseVideo();
      else if (typeof internal.pause === "function") internal.pause();
    } catch (_) {}
  }

  // Play counterpart of _forcePauseInternal — the safety valve for a YT that
  // SHOULD be playing (prop already true, no transition to re-send play) but
  // sits PAUSED/CUED/UNSTARTED (swallowed playVideo, raced pause).
  _forcePlayInternal() {
    this._ytPauseConfirmed = false;
    try {
      const internal =
        this.player && this.player.getInternalPlayer && this.player.getInternalPlayer();
      if (!internal) return;
      if (typeof internal.playVideo === "function") internal.playVideo();
      else if (typeof internal.play === "function") internal.play();
    } catch (_) {}
  }

  // Mirrors the YouTube player state into the ?debugPlayhead=1 overlay.
  _setDebugYtState(s) {
    const dbg = typeof window !== "undefined" && window.__AIDEA_PLAYHEAD_DEBUG;
    if (!dbg) return;
    dbg.perVideo = dbg.perVideo || {};
    const id = this.props.props.id;
    dbg.perVideo[id] = dbg.perVideo[id] || { seeks: 0 };
    dbg.perVideo[id].yt = s;
  }

  // 300ms tick, RATE-based over rolling ~600ms windows: media advance is
  // compared against wall time. Catches full stalls AND the flappy
  // "BUFFERING→PLAYING every few hundred ms" crawl whose episodes are each
  // shorter than the gate's 250ms debounce. The watch also OWNS the release —
  // YT's PLAYING event fires on the first sliver of data, which would reopen
  // the gate long before real progress resumed. Hysteresis: stall below 0.4×
  // realtime, release at/above 0.6×.
  _ytWatchTick() {
    const w = this._ytWatch;
    if (!w) return;
    // Prebuffer phase (preload screen): muted playback fills the buffer.
    // Done when a ~4s head (or 1.5s of playback) exists, or the scene became
    // ready mid-prebuffer (preload cap) — pause + rewind + unmute then.
    if (this._ytPrebuffering) {
      // NOTE: prebuffer playback (muted, off-scene) must NOT set
      // _ytEverPlayed — that killed the boot exception at scene start.
      const buffered = this.state.loadedSeconds || 0;
      const played = this.state.playedSeconds || 0;
      const sceneReady = this.props.props.preloadCompleted && this.props.props.startButtonClicked;
      if (buffered >= 4 || played >= 1.5 || sceneReady) {
        this._finishYtPrebuffer();
      }
      return;
    }
    const shouldAdvance =
      this.state.playing &&
      this.props.props.visorPlaying &&
      this.props.props.show &&
      !this.state.pausedForVideoInteraction;
    if (!shouldAdvance) {
      w.baseAt = null;
      w.baseT = null;
      w.tolerantSince = null;
      // Zombie guard: the YT iframe can keep/START playing on its own while
      // our playing state is false — a seekTo on a cued player auto-plays and
      // the immediately-queued pauseVideo gets swallowed while the player
      // boots (reproduced on scene transitions: YT ran behind the loading
      // screen). The prop is already false, so ReactPlayer never re-sends
      // pause — send it directly whenever the position advances while idle.
      const idleT = this.state.playedSeconds || 0;
      const idleNow = performance.now();
      // Only time the player spent PLAYING can move the position by playback: a wait while
      // loading or cued is not counted towards the next jump.
      let ytState = null;
      try {
        const internal =
          this.player && this.player.getInternalPlayer && this.player.getInternalPlayer();
        if (internal && typeof internal.getPlayerState === "function") {
          ytState = internal.getPlayerState();
        }
      } catch (_) {}
      if (w.idleAt != null && (ytState == null || ytState === 1)) {
        w.idlePlayMs = (w.idlePlayMs || 0) + (idleNow - w.idleAt);
      }
      w.idleAt = idleNow;
      if (w.idleT != null && idleT !== w.idleT) {
        // A jump (the in-point load, a seek) is no playback: only the video's own advance is paused.
        if (this._isPlaybackStep(idleT - w.idleT, w.idlePlayMs || 0)) this._forcePauseInternal();
        w.idlePlayMs = 0;
      }
      w.idleT = idleT;
      // A paused box can never advance — holding its stall would deadlock the
      // gate. Release anything we (or the gate) still hold; a genuine
      // starvation re-reports the moment playback is allowed again.
      if (this._reportedStall && (this._pausedByBufferGate || w.reportedByWatch)) {
        this._reportStall(false);
      }
      w.reportedByWatch = false;
      return;
    }
    w.idleT = null;
    w.idleAt = null;
    w.idlePlayMs = 0;
    const now = performance.now();
    const t = this.state.playedSeconds || 0;
    const rate = this._appliedYoutubeRate();
    // A window measured at one rate only: a rate change starts a new one.
    if (w.baseAt == null || w.rate !== rate) {
      w.baseAt = now;
      w.baseT = t;
      w.rate = rate;
      return;
    }
    const wallSec = (now - w.baseAt) / 1000;
    if (wallSec < 0.6) return;
    const delta = t - w.baseT;
    w.baseAt = now;
    w.baseT = t;
    const jumped = delta !== 0 && !this._isPlaybackStep(delta, wallSec * 1000);
    // After start evidence a jump is a seek, not a stall: the next window judges.
    if (jumped && this._ytEverPlayed) return;
    // Against the rate the player applies: at 0.5× normal playback advances 0.5 s per second.
    // Before start evidence a jump (the in-point load) counts as no progress.
    const ratio = jumped ? 0 : delta / (wallSec * rate);
    // Genuine sustained playback observed → boot exception ends; from here on
    // stalls/buffering may gate the scene. (Set ONLY here — raw playedSeconds
    // was polluted by prebuffer/stale progress reports.)
    if (ratio >= 0.6) this._ytEverPlayed = true;
    if (ratio < 0.4) {
      // Safety valve: a YT that should be playing but sits
      // PAUSED(2)/CUED(5)/UNSTARTED(-1) isn't a network stall — its
      // playVideo was swallowed or raced by a pause. Kick it directly (the
      // prop is already true, ReactPlayer won't re-send play). A genuinely
      // buffering YT just shows its spinner — it never gates the scene.
      try {
        const internal =
          this.player && this.player.getInternalPlayer && this.player.getInternalPlayer();
        const ytState =
          internal && typeof internal.getPlayerState === "function"
            ? internal.getPlayerState()
            : null;
        if (ytState === 2 || ytState === 5 || ytState === -1) {
          this._forcePlayInternal();
        }
      } catch (_) {}
      // Genuine sustained starvation MAY gate the scene — but never the
      // initial boot or a post-seek rebuffer (_ytEverPlayed is re-armed by
      // user seeks and only set after sustained playback). RE-sent every
      // window: reports rejected inside the gate's seek window must not
      // permanently read as "already reported". The overlay label matches
      // the behavior: "stalled" only when actually gating.
      if (this._ytEverPlayed) {
        this._setDebugYtState("stalled");
        w.reportedByWatch = true;
        this._reportStall(true);
      } else {
        // CAPPED tolerance: on a slow connection the boot/seek grace must not
        // become a license to crawl. Escalate to a gating stall when the
        // starvation lasts >3s, or the video already fell >2.5s behind the
        // clock (past the drift-seek limit — a seek would just rebuffer).
        if (w.tolerantSince == null) w.tolerantSince = now;
        const toleratedMs = now - w.tolerantSince;
        const tl = this.props.props.parentBox?.timeline || {};
        const clipStartFrame = typeof tl.startFrame === "number" ? tl.startFrame : 0;
        const playStartFrame =
          typeof tl.playStartFrame === "number" ? tl.playStartFrame : clipStartFrame;
        const frame = this._frameNow();
        const target = this._getTrimStartSec() + Math.max(0, frame - playStartFrame) / 30;
        // A manual video plays on its own time: behind the scene position is not a stall. An
        // autoplay one is measured on the player's own position while it reports PLAYING: the app's
        // position can still be stale at the start. Otherwise the start allowance alone decides.
        const ownT = this.props.state.autoPlay === true ? this._ytPlayingPosition() : null;
        const laggingScene = ownT != null && target - ownT > 2.5;
        if (toleratedMs >= 3000 || laggingScene) {
          this._setDebugYtState("stalled");
          w.reportedByWatch = true;
          this._reportStall(true);
        } else {
          this._setDebugYtState("buffering");
        }
      }
    } else if (ratio >= 0.6) {
      w.tolerantSince = null;
      if (this._reportedStall) {
        w.reportedByWatch = false;
        this._setDebugYtState("playing");
        this._reportStall(false);
        // Gate may STAY closed (comfort hold / other boxes) — stop the
        // recovered YT here so it doesn't run ahead of the frozen clock;
        // gateOpened resumes everything together.
        if (this.props.props.videoBuffering) {
          this._pausedByBufferGate = true;
          this.setState({ playing: false });
        }
      } else {
        this._setDebugYtState(this.state.playing ? "playing" : "paused");
      }
    }
  }

  _handlePlayerPlay() {
    // Consent check — accepting the player's claim unconditionally let an
    // unsolicited start LEGITIMIZE itself (YT auto-plays after a seekTo on a
    // cued player during scene transitions; setting playing:true here
    // disarmed the zombie guard and the video ran audibly behind the loading
    // screen). Veto and pause it directly. While the buffer gate is closed
    // NO element-level play may legitimize itself, gate flag or not — the
    // watchdog's loading-kick play() on a never-started MANUAL video was
    // accepted here and the gate then "resumed" a video the learner never
    // started. Same past a timeline-driven YouTube's scene end (see
    // _ytHeldAtSceneEnd). Deliberate learner starts set state.playing first
    // (playPause), so they are never vetoed.
    const pp = this.props.props;
    const unsolicited =
      !pp.preloadCompleted ||
      !pp.startButtonClicked ||
      !pp.show ||
      pp.videoBuffering ||
      this._ytHeldAtSceneEnd();
    if (unsolicited && !this.state.playing && !this._ytPrebuffering) {
      this._forcePauseInternal();
      return;
    }
    // The sanctioned muted prebuffer playback must not flip our state.
    if (this._ytPrebuffering) return;
    // A start the learner cancelled before it took: its late reports do not restart it until
    // YouTube confirms the pause.
    if (this._ytStartCancelled && !this.state.playing) {
      this._forcePauseInternal();
      return;
    }
    // A pause the gate sent and YouTube never reported is not waited for.
    this._gatePauseEcho = false;
    this._ytHasPlayed = true;
    this._ytRecued = false;
    this.setState({ playing: true, firstEnded: false }, () => {
      let boxx = document.getElementById("box-" + this.props.props.id);
      let controlButtonDiv = boxx && boxx.querySelector(".showwithopacity");
      if (controlButtonDiv) {
        setTimeout(() => {
          controlButtonDiv.classList.remove("showwithopacity");
        }, 1500);
      }
    });
    // Save video progress when playing
    this._saveProgress(this.state.playedSeconds, true);
  }

  // Off the stage the record is the one saved on leaving: late reports must not change it.
  _saveProgress(t, isPlaying) {
    const pp = this.props.props;
    if (typeof pp.saveVideoProgress !== "function" || !pp.show) return;
    pp.saveVideoProgress(pp.id, t, isPlaying);
  }

  _handlePlayerPause() {
    // YouTube took the pause: a later start is a new one.
    this._ytStartCancelled = false;
    this._ytPauseConfirmed = true;
    if (this._suppressNextPause) return;
    // The gate's own pause, reported after the gate already resumed the box.
    if (this._gatePauseEcho) {
      this._gatePauseEcho = false;
      if (this.state.playing) {
        // ReactPlayer sent no play on the reopen (it still took the player for playing).
        const pp = this.props.props;
        const intent = this.props.state.autoPlay ? pp.visorPlaying : true;
        if (
          pp.show &&
          intent &&
          !pp.videoBuffering &&
          pp.preloadCompleted &&
          pp.startButtonClicked &&
          !this.state.pausedForVideoInteraction
        ) {
          this._forcePlayInternal();
        }
        return;
      }
    }
    // A self-stalled YT keeps its play intent while its own stall holds the
    // gate — but YT can emit PAUSED on its own during a long rebuffer.
    // Without the gate flag that pause released the stall as "learner pause",
    // the gate reopened and nothing ever resumed the video (stuck paused
    // while the scene ran). Mark it gate-paused: the watch's deadlock guard
    // releases the stall and gateOpened resumes it with everything else.
    if (this._reportedStall && isYoutubeUrl(this.props.state.url)) {
      this._pausedByBufferGate = true;
    }
    this.setState({ playing: false });
    this._saveProgress(this.state.playedSeconds, false);
  }

  // A finished/exited scene must not keep downloading its streaming video —
  // with preload="auto" the element quietly buffers the ENTIRE file in the
  // background. preload="none" + load() aborts the in-flight fetch; a later
  // replay / scene re-entry re-loads on demand (play()/seek force a fetch).
  // Fully-buffered elements (networkState !== LOADING) are left untouched so
  // a video holding its last frame doesn't visibly reset.
  _stopStreamBufferingIfIdle() {
    const rawUrl = this.props.state.url;
    if (isYoutubeUrl(rawUrl)) return;
    const blobs = typeof window !== "undefined" && window.__visorVideoBlobUrls;
    if (blobs && blobs[rawUrl]) return; // blob-backed: no network to stop
    try {
      const el = document.querySelector(`#box-${this.props.props.id} video`);
      if (!el || el.networkState !== 2 /* NETWORK_LOADING */) return;
      el.preload = "none";
      el.load();
    } catch (_) {}
  }

  componentDidMount() {
    if (isRecorderMode()) {
      // Recorder mode: render() returns <RecorderCanvasVideo /> which owns
      // its own seek registration. Nothing for componentDidMount to do.
      return;
    }
    this._lastCommittedFrame =
      sceneTimeBus.frameSceneId === this._ownSceneId() ? sceneTimeBus.frame : null;
    this._offCommittedFrame = sceneTimeBus.subscribeFrame(() => this._onCommittedFrame());

    // Player-bar master volume: ReactPlayer's controlled `volume` prop would
    // override any external el.volume write, so the master is multiplied
    // into the prop instead (see render) and a change just re-renders.
    this._offMasterVolume = onMasterVolume(() => this.forceUpdate());

    // Load-failure safety: if the video never reaches readyState>=2 within
    // 20s, mark it ended so the scene can complete. Visor-only. An element
    // still deferred by the preload gate (preload="none" while the blob
    // downloads) hasn't even STARTED loading — that's not a failure, re-check
    // later instead of falsely marking it ended.
    if (typeof this.props.props.setVideoEnded === "function") {
      const armLoadFailureTimer = () => {
        this._loadFailureTimer = setTimeout(() => {
          this._loadFailureTimer = null;
          if (this.state.ready) return;
          if (this._videoPreloadAttr() === "none") {
            armLoadFailureTimer();
            return;
          }
          // eslint-disable-next-line no-console
          console.warn(
            "[EnrichedPlayer] video failed to load within 20s — marking ended so the scene can complete",
            this.props.state && this.props.state.url
          );
          this.props.props.setVideoEnded(this.props.props.id);
        }, 20000);
      };
      armLoadFailureTimer();
    }

    // Interactive Video MVP phase 2 — when an InteractivePlugin overlay
    // dispatched by this video is dismissed (EndScreen Continue, or any
    // listener that emits `aidea-video-resume` with our boxId), resume
    // playback. The overlay show side is handled in componentWillUpdate
    // based on timeupdate (state.playedSeconds).
    this._onVideoResume = (evt) => {
      const detail = (evt && evt.detail) || {};
      // Only respond to events targeted at this video box. Missing boxId
      // in the event means "resume any paused-for-interaction video" — we
      // still honour it because the most common dismissal path
      // (EndScreen) doesn't know which video paused.
      if (detail.boxId && detail.boxId !== this.props.props.id) return;
      if (!this.state.pausedForVideoInteraction) return;
      this.setState({ playing: true, pausedForVideoInteraction: null });
    };
    document.addEventListener("aidea-video-resume", this._onVideoResume);

    // Scene end stops every in-scene medium (VisorApp broadcast). Covers what
    // the frame-synced controller can't reach: YouTube (iframe, no <video>)
    // and learner-started manual videos. autoPlay <video> media stays with
    // the controller, which pauses it at the scene bound itself.
    this._onSceneEnded = (evt) => {
      const detail = (evt && evt.detail) || {};
      if (detail.sceneId && detail.sceneId !== this.props.props.currentView) return;
      const manual = this.props.state.autoPlay !== true;
      // `sceneStaysOpen`: the scene showed no scene-end card and does not
      // auto-advance, so it stays on screen with the learner in control. Their
      // manual video is theirs to keep playing (and to start afterwards) —
      // only the scene actually going away stops it (the show true→false
      // branch in componentWillReceiveProps). Timeline-driven media (autoPlay,
      // YouTube autoPlay) still stops at the scene bound as before.
      const keepManual = manual && detail.sceneStaysOpen === true;
      if (keepManual) return;
      if (!manual && isYoutubeUrl(this.props.state.url)) this._ytSceneEndHold = true;
      if (this.state.playing && (isYoutubeUrl(this.props.state.url) || manual)) {
        this.setState({ playing: false });
      }
      // The scene is over — a still-downloading streaming video must stop
      // burning bandwidth behind the CTA.
      this._stopStreamBufferingIfIdle();
    };
    document.addEventListener("aidea-scene-ended", this._onSceneEnded);

    // YouTube stall watch (rate-based — see _ytWatchTick). Covers what YT's
    // own events can't: no BUFFERING during the iframe/SDK's initial load
    // (UNSTARTED), and flappy BUFFERING→PLAYING cycles each shorter than the
    // gate's 250ms debounce, which never froze the scene despite the video
    // crawling far behind the clock.
    if (isYoutubeUrl(this.props.state.url)) {
      this._ytWatch = { baseAt: null, baseT: null, reportedByWatch: false };
      this._ytWatchInterval = setInterval(() => this._ytWatchTick(), 300);
    }

    // Covers the case where the blob decision was already known at mount
    // (revisit / cached meta) — componentDidUpdate handles later arrivals.
    this._maybeKickElementLoad();

    // Auto-zoom camera (imported screen recordings): drive a CSS transform on
    // the raw <video> from step.zoomKeyframes, synced to the clip's own time.
    this._startZoomCamera();

    if (
      this.props.state.autoPlay &&
      this.props.props.preloadCompleted &&
      this.props.props.startButtonClicked &&
      this.props.props.show &&
      this.props.props.visorPlaying &&
      !this.state.firstEnded &&
      !this._hasDeferredPlayStart()
    ) {
      this.setState({ playing: true });
    }
  }

  componentWillUnmount() {
    this._cancelResetStart();
    if (this._offCommittedFrame) this._offCommittedFrame();
    this._detachInPointListeners();
    if (this._offMasterVolume) {
      this._offMasterVolume();
      this._offMasterVolume = null;
    }
    if (this._ytWatchInterval) {
      clearInterval(this._ytWatchInterval);
      this._ytWatchInterval = null;
    }
    if (this._suppressPauseTimer) {
      clearTimeout(this._suppressPauseTimer);
      this._suppressPauseTimer = null;
    }
    this._suppressNextPause = false;
    // Cancel load-failure safety timer
    if (this._loadFailureTimer) {
      clearTimeout(this._loadFailureTimer);
      this._loadFailureTimer = null;
    }
    // Stop the auto-zoom camera rAF loop.
    this._stopZoomCamera();
    if (this._resizeTarget) {
      this._resizeTarget.removeEventListener("resize", this._onVideoResize);
      this._resizeTarget = null;
    }
    // Cancel rVFC loop
    this._rVfcDisposed = true;
    if (this._rVfcHandle !== null) {
      try {
        const internal =
          this.player && this.player.getInternalPlayer && this.player.getInternalPlayer();
        if (internal && typeof internal.cancelVideoFrameCallback === "function") {
          internal.cancelVideoFrameCallback(this._rVfcHandle);
        }
      } catch (_) {}
      this._rVfcHandle = null;
    }
    // Pause before React detaches the <video>: the frame-synced controller plays
    // it imperatively, so a detached-but-playing element keeps its audio running
    // in the background after a scene change.
    try {
      const boxId = this.props.props && this.props.props.id;
      const el = boxId && document.querySelector(`#box-${boxId} video`);
      if (el && typeof el.pause === "function") el.pause();
    } catch (e) {
      /* ignore */
    }
    if (this._onVideoResume) {
      document.removeEventListener("aidea-video-resume", this._onVideoResume);
      this._onVideoResume = null;
    }
    if (this._onSceneEnded) {
      document.removeEventListener("aidea-scene-ended", this._onSceneEnded);
      this._onSceneEnded = null;
    }
    // An unmounting video must never leave the buffer gate frozen.
    if (this._stallTarget && this._stallHandlers) {
      Object.keys(this._stallHandlers).forEach((ev) =>
        this._stallTarget.removeEventListener(ev, this._stallHandlers[ev])
      );
    }
    this._stallTarget = null;
    this._stallHandlers = null;
    if (typeof this.props.props.reportVideoStall === "function") {
      try {
        this.props.props.reportVideoStall(this.props.props.id, false);
      } catch (_) {}
    }
  }

  // ── Auto-zoom camera + browser frame (imported screen recordings) ─────────
  // The recorder bakes a ZoomKeyframe[] click-tracking camera onto each video
  // step (state.zoomKeyframes) and a per-guide browser frame (state.videoFrame).
  // Both are non-destructive playback overlays — the MediaRecorder blob is never
  // touched. Shared with the editor renderer via videoZoom.js.
  _startZoomCamera() {
    const kfs = this.props.state && this.props.state.zoomKeyframes;
    if (!Array.isArray(kfs) || kfs.length === 0) return;
    if (this._zoomCam) return;
    const boxId = this.props.props && this.props.props.id;
    this._zoomCam = createZoomCamera(
      () => (boxId ? document.querySelector(`#box-${boxId} video`) : null),
      kfs
    );
    this._zoomCam.start();
  }

  _stopZoomCamera() {
    if (this._zoomCam) {
      this._zoomCam.stop();
      this._zoomCam = null;
    }
  }

  // Prefer the preloaded blob URL (VisorPreloadManager). Memoized per raw URL:
  // a blob arriving after mount must not swap the src mid-playback. Before
  // playback ever starts, though, a late blob IS adopted — every scene mounts
  // at app open (before preload finishes), so without this the element locks
  // onto the network URL and streams the whole file in parallel with the
  // preloader's blob fetch: the same video downloads twice in full.
  _resolveVideoUrl() {
    const rawUrl = this.props.state.url;
    const blobs = typeof window !== "undefined" && window.__visorVideoBlobUrls;
    const blobUrl = (blobs && blobs[rawUrl]) || null;
    if (this._rawVideoUrl !== rawUrl) {
      this._rawVideoUrl = rawUrl;
      this._resolvedVideoUrl = blobUrl || rawUrl;
    } else if (
      blobUrl &&
      this._resolvedVideoUrl === rawUrl &&
      !this.state.playing &&
      (this.state.playedSeconds || 0) < 0.1
    ) {
      // Idle at the network URL, position 0 — switching src is invisible and
      // aborts the element's own network stream.
      this._resolvedVideoUrl = blobUrl;
    }
    return this._resolvedVideoUrl;
  }

  // While the preloader may still blob this url, the mounted element must not
  // open its own network stream — that duplicates the entire download in
  // parallel with the blob fetch (all scenes mount at app open). preload:"none"
  // keeps it idle; play() still loads on demand. The element is released to
  // normal metadata preload once the preloader decides "stream" (oversized /
  // unknown length / fetch failed → meta.willBlob === false) or the blob is in.
  _videoPreloadAttr() {
    const rawUrl = this.props.state.url;
    if (typeof window === "undefined" || window.location.protocol === "file:") return "metadata";
    if (isYoutubeUrl(rawUrl)) return "metadata";
    const blobs = window.__visorVideoBlobUrls || {};
    if (blobs[rawUrl]) return "metadata"; // src is (or becomes) the blob
    const meta = (window.__visorVideoMeta || {})[rawUrl];
    if (meta && meta.willBlob === false) {
      // Streaming decision. On the ACTIVE scene buffer ahead aggressively —
      // the preloader holds the scene start until this element is playable
      // (P1, _waitForStreamablePlayback). Background scenes stay lazy.
      return this.props.props.show ? "auto" : "metadata";
    }
    return "none";
  }

  _getVideoInteractions() {
    const box = this.props.props?.parentBox;
    const arr = box?.timeline?.interactions;
    return Array.isArray(arr) ? arr : [];
  }

  _maybeFireOverlayShow(nextPlayedSeconds) {
    const interactions = this._getVideoInteractions();
    if (interactions.length === 0) return;
    const prev = this.state.playedSeconds || 0;
    // Edge-trigger: only fire when the playhead crosses startTime in the
    // forward direction this tick. Avoids re-firing every 300ms while the
    // user is still inside an overlay (which is also gated by
    // pausedForVideoInteraction).
    if (this.state.pausedForVideoInteraction) return;
    for (const it of interactions) {
      if (!it || typeof it.startTime !== "number") continue;
      const start = it.startTime;
      // crossed start this tick OR currently inside a tight window
      const crossed = prev < start && nextPlayedSeconds >= start;
      const inside = nextPlayedSeconds >= start && nextPlayedSeconds <= start + 0.35;
      if (!crossed && !inside) continue;
      const detail = {
        boxId: this.props.props.id,
        itemId: (it.params && it.params.itemId) || it.id,
        params: it.params || {},
        startTime: start,
        endTime: typeof it.endTime === "number" ? it.endTime : null,
      };
      this.setState({
        playing: false,
        pausedForVideoInteraction: detail,
      });
      try {
        document.dispatchEvent(
          new CustomEvent("aidea-video-overlay-show", { detail, bubbles: true })
        );
      } catch (_) {
        // jsdom or older runtime — swallow; tests assert via direct mock.
      }
      // One interaction per crossing tick — multiple overlapping starts in
      // the same 300ms slice is a content-authoring bug, not a runtime case.
      break;
    }
  }

  componentWillUpdate(nextProps, nextState) {
    if (nextState.played !== this.state.played) {
      let plugin = this;
      let marks = this.props.props.marks || {};
      let triggerMark = this.props.props.onMarkClicked;
      let triggerArray = this.state.toBeTriggered;
      triggerArray.forEach(function (e, i) {
        // if ((parseFloat(e.value) / 100).toFixed(3) < parseFloat(nextState.played).toFixed(3)) {
        if (parseFloat(convertHMStoSeconds(e.value)) < nextState.playedSeconds) {
          let toBeTriggered = triggerArray;
          triggerMark(plugin.props.props.id, e.value, true);
          toBeTriggered.splice(i, 1);
          plugin.setState({
            toBeTriggered,
            playing: false,
            comingFromMark: true,
          });
        }
      });

      Object.keys(marks).forEach(function (key) {
        let notInArray = true;

        triggerArray.forEach(function (mark) {
          if (mark.id === key) {
            notInArray = false;
          }
        });

        // if(notInArray &&
        //   parseFloat(nextState.played).toFixed(3) <= (parseFloat(marks[key].value) / 100).toFixed(3) &&
        //  parseFloat(parseFloat(nextState.played).toFixed(3)) + 0.05 >= parseFloat((parseFloat(marks[key].value) / 100).toFixed(3))) {
        let valueIntoSeconds = parseFloat(convertHMStoSeconds(marks[key].value), 10);

        if (
          notInArray &&
          parseFloat(nextState.playedSeconds) <= valueIntoSeconds &&
          nextState.playedSeconds + 0.35 >= valueIntoSeconds
        ) {
          let toBeTriggered = triggerArray;
          toBeTriggered.push(marks[key]);
          plugin.setState({ toBeTriggered });
        }
      });
    }

    if (nextProps.props.show && !this.props.props.show && nextState.comingFromMark) {
      // Returning from mark content - restore saved video progress. Playback
      // may only resume on a READY scene: comingFromMark is also (spuriously)
      // set when leaving a scene whose visorPlaying was false, and the
      // ungated restore resumed the video from its saved position BEHIND the
      // next visit's loading screen.
      const savedProgress = this.props.props.getVideoProgress
        ? this.props.props.getVideoProgress(this.props.props.id)
        : { currentTime: 0, isPlaying: false, isEnded: false };
      const sceneReady = nextProps.props.preloadCompleted && nextProps.props.startButtonClicked;

      // A timeline-driven video follows the scene; a manual one keeps its own position.
      const restoreSec = nextProps.state.autoPlay
        ? this._sceneSecToMediaSec(this._frameNow(nextProps) / 30, nextProps)
        : savedProgress.currentTime;
      if (this.player != null) {
        this.player.seekTo(restoreSec, "seconds");
        this.setState({
          comingFromMark: false,
          // A manual video the learner had paused stays paused.
          playing:
            sceneReady &&
            !savedProgress.isEnded &&
            (nextProps.state.autoPlay || savedProgress.isPlaying === true),
          playedSeconds: restoreSec,
          firstEnded: savedProgress.isEnded,
        });
      }
    }
  }

  componentWillMount() {
    if (this.props.state.currentState !== undefined) {
      let initialPoint = parseFloat(convertHMStoSeconds(this.props.state.currentState)) + 0.35;
      this.setState({ initialPoint });
    }
  }

  // Stops the YouTube player (its loaded, paused video is released); the next start plays it again.
  // A trimmed one is re-cued at its in-point instead: a stop drops the embed's start.
  _stopPlayedYoutube() {
    try {
      const internal =
        this.player && this.player.getInternalPlayer && this.player.getInternalPlayer();
      if (!internal || typeof internal.stopVideo !== "function") return;
      const trimStartSec = this._getTrimStartSec();
      const videoId = getYoutubeVideoId(this.props.state.url);
      if (trimStartSec > 0 && videoId && typeof internal.cueVideoById === "function") {
        internal.cueVideoById({ videoId, startSeconds: trimStartSec });
      } else {
        internal.stopVideo();
      }
      this._ytHasPlayed = false;
      this._ytRecued = true;
    } catch (_) {}
  }

  // A trimmed manual YouTube is created at its in-point's whole second (the embed takes no fraction),
  // so a start from YouTube's own controls does not open on the cut part.
  _youtubePlayerVars() {
    if (this.props.state.autoPlay || !isYoutubeUrl(this.props.state.url)) return {};
    const trimStartSec = this._getTrimStartSec();
    return trimStartSec > 0 ? { start: Math.floor(trimStartSec) } : {};
  }

  // The embed start is whole seconds: a manual YouTube with an in-point between them is cued at
  // the exact in-point once per source and in-point. A cue does not play; a video that has played
  // or is playing keeps its position.
  _cueManualYoutubeAtInPoint(internal) {
    if (this.props.state.autoPlay || !isYoutubeUrl(this.props.state.url)) return;
    const trimStartSec = this._getTrimStartSec();
    if (!(trimStartSec > 0) || isWholeSecond(trimStartSec)) return;
    const key = this.props.state.url + "@" + trimStartSec;
    if (this._ytInPointCuedFor === key) return;
    if (this._ytHasPlayed || this.state.playing) return;
    const videoId = getYoutubeVideoId(this.props.state.url);
    if (!videoId || typeof internal.cueVideoById !== "function") return;
    this._ytInPointCuedFor = key;
    try {
      internal.cueVideoById({ videoId, startSeconds: trimStartSec });
    } catch (_) {}
  }

  // The app's play button on a trimmed manual YouTube still before its in-point: start there.
  _startManualYoutubeAtInPoint() {
    if (this.props.state.autoPlay || !isYoutubeUrl(this.props.state.url)) return false;
    const trimStartSec = this._getTrimStartSec();
    if (!(trimStartSec > 0)) return false;
    try {
      const internal =
        this.player && this.player.getInternalPlayer && this.player.getInternalPlayer();
      if (!internal || typeof internal.seekTo !== "function") return false;
      const at =
        typeof internal.getCurrentTime === "function"
          ? internal.getCurrentTime()
          : this.state.playedSeconds || 0;
      if (at >= trimStartSec - HALF_FRAME_SEC) return false;
      // A whole-second in-point is the player's own start: a seek would load the video twice.
      // Not after a restart's re-cue, which the next start may not honour.
      if (!this._ytRecued && at < 0.5 && isWholeSecond(trimStartSec)) {
        return false;
      }
      internal.seekTo(trimStartSec, true);
      return true;
    } catch (_) {
      return false;
    }
  }

  // The YouTube player reports PAUSED or ENDED, and YouTube confirmed a pause after the last play
  // request: no start of its own can follow. Without that confirmation (or when CUED) a play just
  // sent may still take it.
  _ytPlayerAtRest() {
    if (!this._ytPauseConfirmed) return false;
    try {
      const internal =
        this.player && this.player.getInternalPlayer && this.player.getInternalPlayer();
      const st =
        internal && typeof internal.getPlayerState === "function"
          ? internal.getPlayerState()
          : null;
      return st === 0 || st === 2;
    } catch (_) {
      return false;
    }
  }

  // The YouTube player's own position while it reports PLAYING, else null.
  _ytPlayingPosition() {
    try {
      const internal =
        this.player && this.player.getInternalPlayer && this.player.getInternalPlayer();
      if (!internal || typeof internal.getPlayerState !== "function") return null;
      if (internal.getPlayerState() !== 1 || typeof internal.getCurrentTime !== "function") {
        return null;
      }
      const at = internal.getCurrentTime();
      return Number.isFinite(at) && at >= 0 ? at : null;
    } catch (_) {
      return null;
    }
  }

  // A play request: playing, or held back by the closed gate until it reopens.
  _playRequested() {
    return !!(this.state.playing || this._pausedByBufferGate);
  }

  // The learner's start: the in-point seek, and a direct play for a player re-cued on restart.
  _beginLearnerStart() {
    const startedAtInPoint = this._startManualYoutubeAtInPoint();
    // A YouTube stopped on restart: ReactPlayer may still take it for playing and send no play.
    if (this._ytRecued && !startedAtInPoint) this._forcePlayInternal();
    this._ytRecued = false;
  }

  playPause() {
    // A start the gate holds back is still requested: this click cancels it.
    const newPlayingState = !this._playRequested();
    // With the gate closed a start is only recorded: nothing that plays is sent until it opens.
    if (newPlayingState && this.props.props.videoBuffering) {
      this._pausedByBufferGate = true;
      this._heldStart = true;
      this._ytStartCancelled = false;
      this._setUserPaused(false);
      this._setLearnerStart("pending");
      this.setState({});
      return;
    }
    // A held start that is cancelled sent no play: no late report of its own can follow.
    const heldStart = this._heldStart;
    this._heldStart = false;
    if (newPlayingState) this._beginLearnerStart();
    // A pause before the start took (no start evidence yet) may be swallowed by YouTube, unless the
    // player already stands paused or ended.
    this._ytStartCancelled =
      !newPlayingState &&
      !heldStart &&
      isYoutubeUrl(this.props.state.url) &&
      !this._ytEverPlayed &&
      !this._ytPlayerAtRest();
    // A manual toggle takes ownership — the buffer gate must not auto-resume
    // a video the learner deliberately paused (or re-pause one they resumed).
    this._pausedByBufferGate = false;
    this.setState({ playing: newPlayingState });

    // Flag a learner-initiated pause so the frame-synced controller
    // (VisorCanvasSli) stops re-playing an autoPlay video every frame. Cleared
    // when they press play again.
    this._setUserPaused(!newPlayingState);
    this._setLearnerStart(newPlayingState ? "pending" : null);

    // Save video progress when user manually pauses/plays
    this._saveProgress(this.state.playedSeconds, newPlayingState);
  }

  // Restart the video from its (trim-aware) start and clear the ended state so
  // the hover-only replay overlay disappears. Used by the end-of-video overlay
  // shown when the controls are hidden.
  replayVideo() {
    const trimStartSec = this._getTrimStartSec(this.props);
    if (this.player) {
      try {
        this.player.seekTo(trimStartSec, "seconds");
      } catch (e) {}
    }
    this._resetYtBootException();
    this._setUserPaused(false);
    this._setLearnerStart("pending");
    this.setState({
      playedSeconds: trimStartSec,
      played: 0,
      firstEnded: false,
      playing: true,
    });
  }

  onClickFullscreen() {
    if (!this.state.fullscreen) {
      screenfull.request(this.player_wrapper);
    } else {
      screenfull.exit();
    }
    this.setState({ fullscreen: !this.state.fullscreen });
  }

  setVolume(e) {
    this.setState({ volume: parseFloat(e.target.value) });
  }

  setPlaybackRate(e) {
    this.setState({ playbackRate: parseFloat(e.target.value) });
  }

  onSeekMouseDown() {
    this.setState({ seeking: true });
  }

  onSeekChange(e) {
    this.setState({
      played:
        (e.clientX - e.target.getBoundingClientRect().left) /
        e.target.getBoundingClientRect().width,
    });
  }

  onSeekMouseUp(e) {
    const cn = typeof e.target.className === "string" ? e.target.className : "";
    if (
      cn.indexOf("progress-player-input") !== -1 ||
      cn.indexOf("fakeProgress") !== -1 ||
      cn.indexOf("mainSlider") !== -1
    ) {
      const rect = e.target.getBoundingClientRect();
      if (!rect.width) return;
      const pos = (e.clientX - rect.left) / rect.width;
      if (!Number.isFinite(pos) || pos < 0 || pos > 1) return;

      // Header-less WebM: react-player would scale `pos` by its wrong
      // duration (the seekable sliver) — seek in seconds from the real one.
      if (hasInfiniteDuration(this.player) && this.state.duration > 0) {
        try {
          this.player.seekTo(pos * this.state.duration, "seconds");
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn("[EnrichedPlayer] seekTo failed:", err && err.message);
        }
        this.setState({ seeking: false, played: pos });
        return;
      }

      // ReactPlayer multiplies pos by duration; if duration is non-finite
      // (common for webm recordings lacking metadata), seekTo throws.
      const dur =
        (this.player &&
          typeof this.player.getDuration === "function" &&
          this.player.getDuration()) ||
        null;
      if (dur != null && !Number.isFinite(dur)) {
        this.setState({ seeking: false, played: pos });
        return;
      }

      try {
        this.player.seekTo(pos);
      } catch (err) {
        // Swallow non-finite errors from the underlying HTMLMediaElement —
        // the UI already updated `played`, the user sees no crash.
        // eslint-disable-next-line no-console
        console.warn("[EnrichedPlayer] seekTo failed:", err && err.message);
      }
      this.setState({ seeking: false, played: pos });
    }
  }

  _getTrimStartSec(props) {
    const p = props || this.props;
    const box = p.props?.parentBox || p.props?.boxes?.[p.props?.id];
    const trimStart = box?.timeline?.trimStart;
    return typeof trimStart === "number" && trimStart > 0 ? trimStart / 30 : 0;
  }

  // The scene restart's delayed start; a newer restart, a seek, leaving or unmount drops it.
  _cancelResetStart() {
    if (this._resetStartTimer) clearTimeout(this._resetStartTimer);
    this._resetStartTimer = null;
  }

  // autoPlay videos whose play window starts after the clip start are driven by
  // the frame-synced controller (VisorCanvasSli) — the plugin must not
  // self-initiate playback, or the video would flash-play then freeze at mount.
  _hasDeferredPlayStart(props) {
    const p = props || this.props;
    const tl = (p.props?.parentBox || p.props?.boxes?.[p.props?.id])?.timeline;
    // Mounted ahead of its visible start (pre-roll): the controller starts it.
    if (p.props?.prewarm) return true;
    if (!tl || typeof tl.playStartFrame !== "number") return false;
    return tl.playStartFrame > (typeof tl.startFrame === "number" ? tl.startFrame : 0);
  }

  // Marks/clears a learner-initiated pause on the <video> so the frame-synced
  // controller (VisorCanvasSli) stops/resumes re-playing an autoPlay video.
  _setUserPaused(paused) {
    try {
      const el = document.querySelector(`#box-${this.props.props.id} video`);
      if (!el) return;
      if (paused) el.dataset.aideaUserPaused = "1";
      else delete el.dataset.aideaUserPaused;
    } catch (e) {
      /* ignore */
    }
  }

  // Learner start mark on the <video> for the media gate: "pending" → "played"; null clears.
  _setLearnerStart(value) {
    // Clearing is unconditional: the source may have changed kind.
    if (value && (this.props.state.autoPlay === true || isYoutubeUrl(this.props.state.url))) return;
    try {
      const el = document.querySelector(`#box-${this.props.props.id} video`);
      if (!el) return;
      if (value) el.dataset.aideaLearnerStart = value;
      else delete el.dataset.aideaLearnerStart;
    } catch (e) {
      /* ignore */
    }
  }

  _learnerStart() {
    try {
      const el = document.querySelector(`#box-${this.props.props.id} video`);
      return (el && el.dataset && el.dataset.aideaLearnerStart) || null;
    } catch (e) {
      return null;
    }
  }

  _learnerStartPlayed() {
    if (this._learnerStart() === "pending") this._setLearnerStart("played");
  }

  _getTrimEndSec(props) {
    const p = props || this.props;
    const box = p.props?.parentBox || p.props?.boxes?.[p.props?.id];
    const trimEnd = box?.timeline?.trimEnd;
    return typeof trimEnd === "number" ? trimEnd / 30 : null;
  }

  // YouTube drift correction: no <video> element → the frame-synced controller
  // (VisorCanvasSli) can't rate-nudge it, and the YT API only accepts discrete
  // playback rates. Hard-seek back to the frame-derived target when drift
  // exceeds 2s, at most once per 4s (a seek storm looks worse than the drift).
  _maybeCorrectYoutubeDrift(playedSeconds) {
    if (!isYoutubeUrl(this.props.state.url)) return;
    if (this.props.state.autoPlay !== true) return;
    if (!this.state.playing || !this.props.props.visorPlaying) return;
    if (this.props.props.videoBuffering) return;
    // Past the scene end the frame target is frozen — chasing it seeked a
    // learner-replayed embed back every 4s.
    if (this._ytHeldAtSceneEnd()) return;
    // Never during a scene's loading screen: the frame clock reads 0 there,
    // so the "target" would be 0 and the corrector kept seeking a leaked
    // player back to the start in a loop.
    if (!this.props.props.preloadCompleted || !this.props.props.startButtonClicked) return;
    // A box with no timeline/startFrame (AI-generated content) counts as
    // startFrame 0 — skipping it left the video entirely uncorrected.
    const tl = this.props.props.parentBox?.timeline || {};
    const clipStartFrame = typeof tl.startFrame === "number" ? tl.startFrame : 0;
    const playStartFrame =
      typeof tl.playStartFrame === "number" ? tl.playStartFrame : clipStartFrame;
    const frame = this._frameNow();
    if (frame < playStartFrame) return;
    const target = this._getTrimStartSec() + (frame - playStartFrame) / 30;
    if (Math.abs(playedSeconds - target) <= 2) return;
    const now = Date.now();
    if (now - this._lastDriftSeekMs < 4000) return;
    this._lastDriftSeekMs = now;
    if (this.player) {
      try {
        this.player.seekTo(target, "seconds");
      } catch (_) {}
    }
    // Deliberately NO _resetYtBootException() here: resetting on our own
    // corrective seeks let a crawling YT re-arm the boot tolerance every 4s —
    // stall-gating was suppressed forever and the scene never froze no matter
    // how starved YT was. Only USER seeks (timebar/replay/reset) are tolerant.
    const dbg = typeof window !== "undefined" && window.__AIDEA_PLAYHEAD_DEBUG;
    if (dbg) {
      dbg.hardSeeks++;
      dbg.perVideo = dbg.perVideo || {};
      const id = this.props.props.id;
      dbg.perVideo[id] = dbg.perVideo[id] || { seeks: 0 };
      dbg.perVideo[id].seeks++;
    }
  }

  onProgress(state) {
    // if (!this.state.seeking) {
    // Interactive Video MVP phase 2 — check interactions BEFORE setState so
    // the prev/next comparison sees the actual delta. _maybeFireOverlayShow
    // sets its own state (pausedForVideoInteraction + playing:false) when
    // it pauses for an overlay, which the merged setState below will keep.
    if (typeof state.playedSeconds === "number") {
      this._maybeFireOverlayShow(state.playedSeconds);
      this._maybeCorrectYoutubeDrift(state.playedSeconds);
    }
    // react-player's `played` fraction divides by its own (wrong) duration
    // for header-less WebM — recompute it from the probed length.
    if (this._probedDuration && typeof state.playedSeconds === "number") {
      state = { ...state, played: Math.min(1, state.playedSeconds / this._probedDuration) };
    }
    this.setState(state);
    // Save video progress
    if (state.playedSeconds) this._saveProgress(state.playedSeconds, this.state.playing);
    // trimEnd kontrolü: trimEnd saniyesine gelince durdur
    const trimEndSec = this._getTrimEndSec();
    if (trimEndSec !== null && state.playedSeconds >= trimEndSec && this.state.playing) {
      this.setState({ playing: false, firstEnded: true });
      if (this.props.props.setVideoEnded) {
        this.props.props.setVideoEnded(this.props.props.id, true);
      }
    }
    // }
  }

  componentWillReceiveProps(nextProps) {
    // Playhead moved back (seek / replay / another scene) or the scene was
    // re-entered — the scene-end hold no longer applies.
    if (
      this._ytSceneEndHold &&
      ((nextProps.props.visorFrameTime || 0) < (this.props.props.visorFrameTime || 0) ||
        (!this.props.props.show && nextProps.props.show))
    ) {
      this._ytSceneEndHold = false;
    }

    if (nextProps.state.controls === true && this.state.controls !== this.props.state.controls) {
      this.setState({ controls: true });
    } else if (
      nextProps.state.controls === false &&
      this.state.controls !== this.props.state.controls
    ) {
      this.setState({ controls: false });
    }

    if (!this.props.props.show && nextProps.props.show) {
      if (this.state.comingFromMark) {
        // Returning from mark content - restore saved video progress. Same
        // READY-scene gating as the componentWillUpdate copy above — the
        // ungated restore was the loading-screen playback leak.
        const savedProgress = this.props.props.getVideoProgress
          ? this.props.props.getVideoProgress(this.props.props.id)
          : { currentTime: 0, isPlaying: false, isEnded: false };
        const restoreReady = nextProps.props.preloadCompleted && nextProps.props.startButtonClicked;

        // A timeline-driven video follows the scene; a manual one keeps its own position.
        const restoreSec = nextProps.state.autoPlay
          ? this._sceneSecToMediaSec(this._frameNow(nextProps) / 30, nextProps)
          : savedProgress.currentTime;
        if (this.player != null) {
          this.player.seekTo(restoreSec, "seconds");
          this.setState({
            comingFromMark: false,
            // A manual video the learner had paused stays paused.
            playing:
              restoreReady &&
              !savedProgress.isEnded &&
              (nextProps.state.autoPlay || savedProgress.isPlaying === true),
            playedSeconds: restoreSec,
            firstEnded: savedProgress.isEnded,
          });
        } else {
          this.setState({ comingFromMark: false });
        }
      } else {
        // Scene navigation - restart from trimStart if set. Playback may only
        // begin when the target scene is actually READY — a scene change
        // re-runs preload (loading screen) and starting here made YouTube/
        // videos play behind it. When not ready yet, the preloadCompleted
        // rise (componentDidUpdate ready-transition) starts autoPlay media.
        const sceneReady = nextProps.props.preloadCompleted && nextProps.props.startButtonClicked;
        const trimStartSec = this._getTrimStartSec(nextProps);
        // A new visit drops the learner start.
        this._setLearnerStart(null);
        if (this.player != null) {
          this.player.seekTo(trimStartSec, "seconds");
          this.setState({
            playing: sceneReady && nextProps.state.autoPlay,
            playedSeconds: trimStartSec,
            firstEnded: false,
          });
        } else {
          this.setState({ playing: sceneReady && nextProps.state.autoPlay });
        }
      }
    } else if (this.props.props.show && !nextProps.props.show) {
      // Save current progress when leaving the page
      if (this.props.props.saveVideoProgress && this.player != null) {
        this.props.props.saveVideoProgress(
          this.props.props.id,
          this.state.playedSeconds,
          this.state.playing
        );
      }
      // Detect if we're going to a contained view (mark click):
      // When a contained view opens, visorPlaying is false (VisorApp sets it).
      // In a normal scene change, changeStateFunc always sets visorPlaying to true.
      const isGoingToContainedView = !nextProps.props.visorPlaying;
      this._cancelResetStart();
      // A scene change restarts it from its in-point on return; a paused YouTube left in the
      // kept-alive layer held the next YouTube start in buffering.
      if (!isGoingToContainedView && isYoutubeUrl(this.props.state.url)) {
        try {
          const internal =
            this.player && this.player.getInternalPlayer && this.player.getInternalPlayer();
          if (internal && typeof internal.stopVideo === "function") internal.stopVideo();
        } catch (_) {}
      }
      this.setState({
        playing: false,
        comingFromMark: isGoingToContainedView || this.state.comingFromMark,
      });
    }

    // Timebar seek — visorSeekTo changes from null to a seconds value.
    // AutoPlay (timeline-driven) media only: a manual video's position is
    // learner-owned — a scene seek must not move, pause or rebuffer it.
    if (
      nextProps.state.autoPlay &&
      nextProps.props.visorSeekTo !== null &&
      nextProps.props.visorSeekTo !== undefined &&
      nextProps.props.visorSeekTo !== this.props.props.visorSeekTo &&
      nextProps.props.show
    ) {
      const seekSec = this._sceneSecToMediaSec(nextProps.props.visorSeekTo, nextProps);
      // Suppress the browser pause event that fires during seek
      if (this._suppressPauseTimer) clearTimeout(this._suppressPauseTimer);
      this._suppressNextPause = true;
      this._suppressPauseTimer = setTimeout(() => {
        this._suppressNextPause = false;
        this._suppressPauseTimer = null;
      }, 600);
      if (this.player != null) {
        this.player.seekTo(seekSec, "seconds");
      }
      // Timebar seek → the post-seek rebuffer must not freeze-cycle the scene.
      this._resetYtBootException();
      // A restart's pending start belongs to the old position.
      this._cancelResetStart();
      this.setState({
        playedSeconds: seekSec,
        firstEnded: false,
        playing: false,
      });
    }

    if (!this.props.props.resetTriggered && nextProps.props.resetTriggered) {
      // Reset triggered - restart from trimStart
      const trimStartSec = this._getTrimStartSec(nextProps);
      const manualYoutube = !nextProps.state.autoPlay && isYoutubeUrl(nextProps.state.url);
      // A manual YouTube that is not loaded gets no seek: that left it unstarted, ignoring clicks.
      if (this.player && (!manualYoutube || this._ytHasPlayed)) {
        this.player.seekTo(trimStartSec, "seconds");
      }
      // A manual YouTube that has played is stopped, not left loaded and paused: that kept the
      // scene's next YouTube from playing (YT-04).
      if (manualYoutube && this._ytHasPlayed) {
        this._stopPlayedYoutube();
      }
      this._resetYtBootException();
      // A restart drops the learner start.
      this._setLearnerStart(null);
      this.setState(
        {
          playedSeconds: trimStartSec,
          played: 0,
          firstEnded: false,
          playing: false,
        },
        () => {
          this._cancelResetStart();
          this._resetStartTimer = setTimeout(() => {
            this._resetStartTimer = null;
            // Only start playing if the video is visible (in the current
            // scene) AND the scene is ready — never behind a loading screen.
            // A manual video waits for the learner; a later play window for the controller.
            if (
              this.props.state.autoPlay &&
              !this._hasDeferredPlayStart() &&
              this.props.props.show &&
              this.props.props.preloadCompleted &&
              this.props.props.startButtonClicked
            ) {
              this.setState({
                playing:
                  this.props.props.visorPlaying !== undefined
                    ? this.props.props.visorPlaying
                    : true,
              });
            }
          }, 150);
        }
      );
    }

    // Scene pause stops the video regardless of autoPlay — a stopped scene must
    // not keep a video playing. Scene play, however, only auto-starts autoPlay
    // videos; a manual video stays learner-controlled and does not auto-start.
    // Exception: a scene SEEK dips visorPlaying for ~150ms (visorSeekTo rides
    // along to mark it) — a manual video must play right through it; the rise
    // wouldn't restart it (autoPlay-only), so pausing here lost it for good.
    const seekDip =
      nextProps.props.visorSeekTo !== null && nextProps.props.visorSeekTo !== undefined;
    if (this.props.props.visorPlaying && !nextProps.props.visorPlaying) {
      if (nextProps.state.autoPlay || !seekDip) {
        this.setState({ playing: false });
      }
    } else if (
      nextProps.state.autoPlay &&
      !this.props.props.visorPlaying &&
      nextProps.props.visorPlaying &&
      nextProps.props.show &&
      // A scene change flips visorPlaying while its preload (loading screen)
      // is still running — playback may only start on a READY scene.
      nextProps.props.preloadCompleted &&
      nextProps.props.startButtonClicked
    ) {
      if (
        !this.state.firstEnded &&
        !this._hasDeferredPlayStart(nextProps) &&
        !this._ytHeldAtSceneEnd()
      ) {
        // Scene-level play overrides any individual learner pause.
        this._setUserPaused(false);
        this.setState({ playing: true });
      }
    }

    const wasReadyToPlay = this.props.props.preloadCompleted && this.props.props.startButtonClicked;
    const isReadyToPlay = nextProps.props.preloadCompleted && nextProps.props.startButtonClicked;
    if (
      !wasReadyToPlay &&
      isReadyToPlay &&
      nextProps.state.autoPlay &&
      nextProps.props.show &&
      nextProps.props.visorPlaying &&
      !this.state.firstEnded &&
      !this._hasDeferredPlayStart(nextProps)
    ) {
      this.setState({ playing: true });
    }
  }

  // YouTube (iframe) media has no <video> element, so the frame-synced
  // controller in VisorCanvasSli cannot drive a deferred play window
  // (playStartFrame > startFrame) — without this the video never starts.
  // Edge-triggered on the playhead crossing so a learner pause isn't
  // re-overridden every frame.
  _maybeStartDeferredYoutube(prevFrame, frame) {
    if (!this._hasDeferredPlayStart()) return;
    if (!isYoutubeUrl(this.props.state.url)) return;
    if (
      !this.props.state.autoPlay ||
      !this.props.props.preloadCompleted ||
      !this.props.props.startButtonClicked ||
      !this.props.props.show ||
      !this.props.props.visorPlaying ||
      this.state.playing ||
      this.state.firstEnded ||
      this.state.pausedForVideoInteraction
    ) {
      return;
    }
    const tl = this.props.props.parentBox?.timeline;
    if (!tl || typeof tl.playStartFrame !== "number") return;
    if (prevFrame >= tl.playStartFrame || frame < tl.playStartFrame) return;

    const trimStartSec = this._getTrimStartSec();
    if (this.player && trimStartSec > 0 && (this.state.playedSeconds || 0) < trimStartSec) {
      try {
        this.player.seekTo(trimStartSec, "seconds");
      } catch (e) {
        /* ignore */
      }
    }
    this.setState({ playing: true });
  }

  // A YouTube iframe is invisible to the frame-synced controller in
  // VisorCanvasSli, which is what applies the learner's playback rate to
  // <video> media — so the rate has to be pushed into the iframe API here.
  // VisorApp already caps the rate at YOUTUBE_MAX_SPEED for scenes holding a
  // YouTube box; the clamp below guards any rate that slips past.
  _applyYoutubePlaybackRate() {
    if (!isYoutubeUrl(this.props.state.url)) return;
    const rate = Number(this.props.props.playbackRate) || 1;
    const internal =
      this.player && this.player.getInternalPlayer && this.player.getInternalPlayer();
    if (!internal || typeof internal.setPlaybackRate !== "function") return;

    let target = rate;
    if (typeof internal.getAvailablePlaybackRates === "function") {
      const available = internal.getAvailablePlaybackRates() || [];
      if (available.length) {
        target = available.reduce((best, r) => (r <= rate && r > best ? r : best), available[0]);
      }
    }
    if (this._appliedYtRate === target) return;
    this._appliedYtRate = target;
    try {
      internal.setPlaybackRate(target);
    } catch (e) {
      /* iframe not ready — the next update retries */
    }
  }

  // The rate the YouTube player runs at (the player's own report first).
  _appliedYoutubeRate() {
    try {
      const internal =
        this.player && this.player.getInternalPlayer && this.player.getInternalPlayer();
      const r =
        internal && typeof internal.getPlaybackRate === "function" && internal.getPlaybackRate();
      if (r > 0) return r;
    } catch (_) {}
    return this._appliedYtRate || Number(this.props.props.playbackRate) || 1;
  }

  // Scene-end cutoff for EVERY medium this plugin plays. The frame-synced
  // controller cuts <video> media it drives, but YouTube iframes and boxes
  // it skips would keep playing (audibly) behind the scene-end CTA.
  // Edge-triggered on the crossing so a learner who replays the video after
  // the scene end isn't fought every frame.
  _maybeStopMediaAtSceneEnd(prevFrame, frame) {
    if (!this.state.playing) return;
    const box = this.props.props.parentBox;
    const tl = box?.timeline;
    const studio = typeof IdealStudio !== "undefined" ? IdealStudio : window.IdealStudio;
    // This bound belongs to a scene that CLOSES at its end (auto-advance or an
    // opted-in scene-end card) — there the video must not outlive the scene.
    // On a scene that stays on screen a manual video is learner-owned: one
    // that was already rolling when the scene duration ran out keeps rolling,
    // and its authored end frame does not cut it either. What still stops it:
    // the media's own end (trimEnd/ended), leaving the stage (the box unmounts
    // and pauses the element), a scene change, and a visor pause.
    const manualStaysOpen =
      this.props.state.autoPlay !== true &&
      !sceneEndClosesScene({
        toolbar: studio?.State?.viewToolbarsById?.[box?.parent],
        hasNext: !!getNextValidPage(box?.parent),
      });
    if (manualStaysOpen) return;
    const sceneDur = studio?.State?.navItemsById?.[box?.parent]?.duration;
    let endBound = typeof tl?.endFrame === "number" ? tl.endFrame : null;
    if (typeof sceneDur === "number" && sceneDur > 0) {
      endBound = endBound === null ? sceneDur : Math.min(endBound, sceneDur);
    }
    if (endBound === null) return;
    if (prevFrame >= endBound || frame < endBound) return;
    this.setState({ playing: false });
  }

  componentDidUpdate(prevProps, prevState) {
    // A play request since YouTube's last pause: the player may still take it.
    if (prevState && !prevState.playing && this.state.playing) this._ytPauseConfirmed = false;
    // A new source carries no start evidence or cancelled start over, whatever its media kind.
    if (prevProps.state && prevProps.state.url !== this.props.state.url) {
      this._ytStartCancelled = false;
      this._ytHasPlayed = false;
      this._ytInPointCuedFor = null;
      this._ytRecued = false;
      this._resetYtBootException();
      this._setLearnerStart(null);
    }
    this._maybeKickElementLoad();
    this._maybeStartYtPrebuffer();
    // Live playback follows the committed frame (_onCommittedFrame).
    if (isRecorderMode()) {
      const prevFrame = prevProps.props.visorFrameTime || 0;
      const frame = this.props.props.visorFrameTime || 0;
      this._maybeStartDeferredYoutube(prevFrame, frame);
      this._maybeStopMediaAtSceneEnd(prevFrame, frame);
    }
    this._applyYoutubePlaybackRate();

    // A NEW loading phase (scene change re-running preload) stops playback —
    // defensive twin of the atomic preloadCompleted:false in the nav
    // setState: nothing may keep playing behind a loading screen, whatever
    // ordering race let it start. The ready-transition below resumes autoPlay
    // media when the preload completes.
    if (
      prevProps.props.preloadCompleted &&
      !this.props.props.preloadCompleted &&
      this.state.playing
    ) {
      this.setState({ playing: false });
      this._forcePauseInternal();
    }

    // A deliberately paused medium can never emit its recovery signal
    // (YouTube: PLAYING; file: loading may be browser-suspended while paused)
    // — release the stall on ANY pause transition (learner pause, scene end,
    // seek, scene navigation) or the gate stays closed forever. Exception: a
    // GATE-initiated pause must keep the stall held — the rate watch releases
    // it from buffer growth once enough data is in.
    if (
      prevState.playing &&
      !this.state.playing &&
      this._reportedStall &&
      !this._pausedByBufferGate
    ) {
      this._reportStall(false);
    }
    // Overlay state + bandwidth: leaving the stage stops a still-downloading
    // streaming video's background fetch.
    if (isYoutubeUrl(this.props.state.url) && prevState.playing !== this.state.playing) {
      this._setDebugYtState(this.state.playing ? "playing" : "paused");
    }
    if (prevProps.props.show && !this.props.props.show) {
      this._stopStreamBufferingIfIdle();
    }

    // Buffer gate ↔ every medium: while the gate is closed NOTHING may play —
    // the frozen clock stops the frame-synced controller's renders, so a
    // video left (or started) playing free-runs ahead of the scene. Three
    // rules, transition-driven so they work render-free:
    //   1. gate closes → pause anything playing;
    //   2. anything that STARTS while the gate is closed (scene entry, ready
    //      transition, learner play) is immediately deferred;
    //   3. gate reopens → resume only what the gate itself paused/deferred.
    // Exception: a YouTube box whose OWN buffering closed the gate stays
    // playing — pausing it would prevent the PLAYING state that reopens the
    // gate (file videos release via canplaythrough, so they're safe to pause).
    const gateClosed = !prevProps.props.videoBuffering && this.props.props.videoBuffering;
    const gateOpened = prevProps.props.videoBuffering && !this.props.props.videoBuffering;
    const startedWhileFrozen =
      this.props.props.videoBuffering && !prevState.playing && this.state.playing;
    // The gate pauses EVERYTHING — file videos included, starving or not: a
    // paused <video> with preload="auto" keeps buffering (rs climbs and
    // canplaythrough fires while paused), and its held stall is released by
    // the DOM listener / synthetic sweep at rs>=4. ONE exemption: a YouTube
    // whose own watch-detected stall holds the gate keeps its play intent —
    // paused, the watch could never see it advance and release (the only
    // reliable YT recovery signal). It re-pauses via the watch's release
    // branch if the gate is still closed then.
    const ytSelfStalled = isYoutubeUrl(this.props.state.url) && this._reportedStall;
    if ((gateClosed || startedWhileFrozen) && this.state.playing && !ytSelfStalled) {
      this._pausedByBufferGate = true;
      // YouTube reports this pause later, possibly after the gate has resumed the box.
      if (isYoutubeUrl(this.props.state.url)) this._gatePauseEcho = true;
      this.setState({ playing: false });
    }
    // A learner start held back by the gate (no command sent yet) ends with a scene pause or leaving
    // the scene; a video the gate paused while playing keeps its resume.
    // So does an unplayed manual start the gate paused.
    const pendingStart =
      this._heldStart || (this._pausedByBufferGate && this._learnerStart() === "pending");
    if (
      pendingStart &&
      ((prevProps.props.visorPlaying && !this.props.props.visorPlaying) ||
        (prevProps.props.show && !this.props.props.show))
    ) {
      this._heldStart = false;
      this._pausedByBufferGate = false;
      this._setLearnerStart(null);
      this.setState({});
    }
    if (gateOpened && this._pausedByBufferGate) {
      this._pausedByBufferGate = false;
      const heldStart = this._heldStart;
      this._heldStart = false;
      if (
        this.props.props.show &&
        this.props.props.visorPlaying &&
        // A scene change clears the gate (videoBuffering → false) while its
        // preload runs — resuming here restarted media under the loading screen.
        this.props.props.preloadCompleted &&
        this.props.props.startButtonClicked &&
        !this.state.playing &&
        !this.state.firstEnded &&
        !this.state.pausedForVideoInteraction &&
        // The scene end clears the gate in the same commit that stops a YT.
        !this._ytHeldAtSceneEnd()
      ) {
        // A learner start the closed gate held back begins now, at the in-point.
        if (heldStart) this._beginLearnerStart();
        this.setState({ playing: true });
      }
    }

    const wasReadyToPlay = prevProps.props.preloadCompleted && prevProps.props.startButtonClicked;
    const isReadyToPlay = this.props.props.preloadCompleted && this.props.props.startButtonClicked;

    if (
      !wasReadyToPlay &&
      isReadyToPlay &&
      this.props.state.autoPlay &&
      this.props.props.show &&
      this.props.props.visorPlaying &&
      !this.state.firstEnded &&
      !this.state.playing &&
      !this._hasDeferredPlayStart()
    ) {
      this.setState({ playing: true });
    }

    if (
      !prevProps.props.show &&
      this.props.props.show &&
      this.props.props.preloadCompleted &&
      this.props.props.startButtonClicked &&
      this.props.state.autoPlay &&
      this.props.props.visorPlaying &&
      !this.state.firstEnded &&
      !this.state.playing &&
      !this._hasDeferredPlayStart()
    ) {
      this.setState({ playing: true });
    }
  }

  onReady(e) {
    if (this.player !== undefined) {
      // Priority: initialPoint > start from 0. Starting is still gated on a
      // READY scene — onReady can fire behind a loading screen.
      if (this.state.initialPoint !== undefined) {
        this.player.seekTo(this.state.initialPoint);
        this.setState({
          initialPoint: undefined,
          playing: !!(
            this.props.props.preloadCompleted &&
            this.props.props.startButtonClicked &&
            !this._ytStartCancelled
          ),
          ready: true,
        });
      } else {
        this.setState({ ready: true });
      }
    } else {
      this.setState({ ready: true });
    }
    // Clear load-failure safety timer — video is ready
    if (this._loadFailureTimer) {
      clearTimeout(this._loadFailureTimer);
      this._loadFailureTimer = null;
    }

    const internal =
      this.player && this.player.getInternalPlayer && this.player.getInternalPlayer();
    if (!internal) return;
    // The rest is for a <video> element; YouTube has its own stall watch and no element events.
    if (isYoutubeUrl(this.props.state.url)) {
      this._cueManualYoutubeAtInPoint(internal);
      return;
    }

    // In-point guard: reveal once the element displays trimStart (see
    // _inPointGuardNeeded). The element may already sit there (blob src,
    // loadedmetadata seek) — check now, else on the next seek/frame.
    if (!this.state.atInPoint && typeof internal.addEventListener === "function") {
      if (this.props.props.show) this._seekToTimelineTarget(internal);
      this._checkInPoint(internal);
      if (!this._inPointHandlers) {
        const check = () => this._checkInPoint(internal);
        this._inPointHandlers = { seeked: check, timeupdate: check, playing: check };
        Object.keys(this._inPointHandlers).forEach((ev) =>
          internal.addEventListener(ev, this._inPointHandlers[ev])
        );
        this._inPointTarget = internal;
      }
    }

    // Buffer-stall reporting (visor only). "waiting" = playback actually
    // stopped for lack of data; "stalled" is too noisy (fires with a full
    // buffer too).
    if (
      typeof this.props.props.reportVideoStall === "function" &&
      typeof internal.addEventListener === "function" &&
      !this._stallHandlers
    ) {
      // Routed through _reportStall so _reportedStall marks this box as the
      // gate-holder — the gate coupling must never pause the starving box
      // (a paused element's loading gets browser-suspended and canplaythrough
      // never fires → permanently closed gate).
      const report = (stalled) => () => this._reportStall(stalled);
      // Release on canplaythrough, not canplay — a sliver of buffer would
      // resume → starve → stall again. "playing" covers external resumes.
      this._stallHandlers = {
        waiting: report(true),
        canplaythrough: report(false),
        playing: () => {
          this._learnerStartPlayed();
          this._reportStall(false);
        },
      };
      Object.keys(this._stallHandlers).forEach((ev) =>
        internal.addEventListener(ev, this._stallHandlers[ev])
      );
      this._stallTarget = internal;
    }

    const readDims = () => {
      const w = internal.videoWidth;
      const h = internal.videoHeight;
      if (w && h) this.setState({ naturalRatio: w / h });
    };
    if (internal.videoWidth && internal.videoHeight) {
      readDims();
    } else if (typeof internal.addEventListener === "function") {
      internal.addEventListener("loadedmetadata", readDims, { once: true });
    }
    // Screen recordings can change resolution mid-stream (a tab capture's first
    // frame often differs from the rest) — the look's geometry must follow the
    // frames actually shown, or the window letterboxes. Look boxes only.
    if (
      (this.props.state.videoWallpaper || this.props.state.videoFrame) &&
      typeof internal.addEventListener === "function" &&
      this._resizeTarget !== internal
    ) {
      if (this._resizeTarget) this._resizeTarget.removeEventListener("resize", this._onVideoResize);
      this._onVideoResize = readDims;
      this._resizeTarget = internal;
      internal.addEventListener("resize", readDims);
    }

    // Frame-accurate progress via requestVideoFrameCallback (visor only, graceful fallback).
    // Reports through the same saveVideoProgress prop used by onProgress/onPause/onPlay.
    if (
      !isRecorderMode() &&
      typeof this.props.props.saveVideoProgress === "function" &&
      typeof internal.requestVideoFrameCallback === "function" &&
      // onReady repeats: one loop per element; a new element ends the old loop.
      (this._rVfcTarget !== internal || this._rVfcDisposed)
    ) {
      this._rVfcDisposed = false;
      this._rVfcTarget = internal;
      const loop = (this._rVfcLoop = (this._rVfcLoop || 0) + 1);
      const scheduleNext = () => {
        if (this._rVfcDisposed || loop !== this._rVfcLoop) return;
        this._rVfcHandle = internal.requestVideoFrameCallback((now, meta) => {
          if (!this._rVfcDisposed && loop === this._rVfcLoop) {
            const t = meta.mediaTime;
            // Throttle to ~4/sec (250ms)
            if (now - this._rVfcLastReportTime >= 250) {
              this._rVfcLastReportTime = now;
              this._saveProgress(t, this.state.playing);
            }
            scheduleNext();
          }
        });
      };
      scheduleNext();
    }
  }

  videoPlayScreenOn() {
    let mainContent = document.getElementById("maincontent");
    let div = document.createElement("div");
    div.setAttribute("id", "videoPlayScreen");
    div.setAttribute(
      "style",
      "width: 100%; height: 100%; position: absolute; background: #222222; left: 0%; top: 0%;"
    );
    let button = document.createElement("div");
    button.setAttribute(
      "style",
      "position: absolute;left: 50%;top: 50%;transform : translate(-50%,-50%);display: flex;width: 170px;height: 170px;justify-content: center;align-items: center;border-radius: 50%;background: #b1b1b1; cursor: pointer;"
    );
    button.onmouseover = (e) => {
      button.style.background = "gray";
    };
    button.onmouseout = (e) => {
      button.style.background = "#b1b1b1";
    };
    button.onclick = () => {
      this.setState({ playButtonClicked: true }, this.videoPlayScreenOut());
    };
    let span = document.createElement("span");
    span.setAttribute(
      "style",
      "display: flex; width: 0; height: 0; border-style: solid; border-width: 50px 0 50px 75px; border-color: transparent transparent transparent #eab700; border-radius: 3px; margin-left: 20px;"
    );
    button.append(span);
    div.append(button);
    if (mainContent != null) {
      mainContent.append(div);
    }
  }

  videoPlayScreenOut() {
    let videoPlayScreen = document.getElementById("videoPlayScreen");
    videoPlayScreen.remove();
  }

  render() {
    if (isRecorderMode()) {
      const tl = this.props.props.parentBox && this.props.props.parentBox.timeline;
      const startFrame = typeof tl?.startFrame === "number" ? tl.startFrame : 0;
      // Same scene→media mapping as _sceneSecToMediaSec: autoPlay clips are
      // offset by their play window, and split/trimmed pieces by the source
      // in-point — without it the recorder rendered every piece from t=0.
      const playStartFrame =
        this.props.state.autoPlay === true && typeof tl?.playStartFrame === "number"
          ? tl.playStartFrame
          : startFrame;
      return (
        <div
          style={{ width: "100%", height: "100%" }}
          className="enriched-player-wrapper"
          id={"box-" + this.props.props.id + "-recorder-canvas"}
        >
          <RecorderCanvasVideo
            url={this.props.state.url}
            clipStartSec={playStartFrame / 30}
            trimStartSec={this._getTrimStartSec()}
            trimEndSec={this._getTrimEndSec()}
            loop={!!this.props.state.loop}
            boxId={this.props.props.id}
            registerSeek={registerSeek}
            unregisterSeek={unregisterSeek}
            onReady={this.props.props.onVideoCanPlayThrough}
          />
        </div>
      );
    }

    let marks = this.props.props.marks || {};
    let markElements = Object.keys(marks).map((id) => {
      let secondsValue = convertHMStoSeconds(marks[id].value);
      let duration = this.state.duration;
      let value = (secondsValue * 100) / duration + "%";
      let title = marks[id].title;
      let color = marks[id].color;
      let isPopUp = marks[id].connectMode === "popup";
      let noTrigger = false;
      let isVisor = true;
      return (
        <div
          key={id}
          className="videoMark"
          style={{
            background: color || "#eab700",
            left: value,
            position: "absolute",
          }}
        >
          <Mark
            style={{ position: "relative", top: "-1.7em", left: "-0.75em" }}
            color={color || "#eab700"}
            idKey={id}
            title={title}
            isVisor={isVisor}
            isPopUp={isPopUp}
            markConnection={marks[id].connection}
            noTrigger={noTrigger}
            onMarkClicked={() => {
              this.props.props.onMarkClicked(this.props.props.id, marks[id].value, true);
            }}
          />
        </div>
      );
    });

    // Imported screen recordings carry the recorder's look (wallpaper +
    // browser frame) — see videoZoom.js. null → plain video, as before.
    const geo = videoLookGeometry(this.props.state, this.state.naturalRatio);
    const playerEl = (
      <ReactPlayer
        ref={(player) => {
          this.player = player;
        }}
        style={
          geo ? videoLookPlayerStyle(geo) : { width: "100%", height: "100%", position: "absolute" }
        }
        height="100%"
        width="100%"
        playsinline
        config={{
          youtube: { playerVars: this._youtubePlayerVars() },
          file: {
            attributes: {
              disablePictureInPicture: true,
              preload: this._videoPreloadAttr(),
              // Seek to the in-point before the first frame is decoded.
              onLoadedMetadata: this._onElementLoadedMetadata,
            },
          },
        }}
        progressInterval={PROGRESS_INTERVAL_MS}
        url={this._resolveVideoUrl()}
        // Pre-roll never plays, whichever path set the state.
        playing={this.state.playing && !this.props.props.prewarm}
        loop={!!this.props.state.loop}
        volume={isRecorderMode() ? 0 : this.state.volume * getMasterVolume()}
        // Volume 0 must be MUTED too: autoplay before a user gesture is only
        // allowed for muted media (talking-avatar videos are inserted at 0).
        muted={this.state.volume === 0}
        onPlay={() => this._handlePlayerPlay()}
        onPause={() => this._handlePlayerPause()}
        onEnded={() => {
          // Looping playback (animated-image boxes) must never stop or
          // re-show controls — the native/react-player loop keeps it going.
          if (this.props.state.loop) return;
          let boxx = document.getElementById("box-" + this.props.props.id);
          let controlButtonDiv = boxx.querySelector(".visorControls");
          if (controlButtonDiv) {
            setTimeout(() => {
              controlButtonDiv.classList.add("showwithopacity");
            }, 1500);
          }

          this.setState({ playing: false, firstEnded: true }, () => {
            // Save video progress as ended at its final position
            this._saveProgress(this.state.playedSeconds, false);
            this.props.props.setVideoEnded(this.props.props.id);
          });
        }}
        onProgress={this.onProgress.bind(this)}
        onDuration={(duration) => {
          // WebM without a Duration header: react-player reports the seekable
          // sliver, not the length — probe the real one (videoZoom.js).
          if (duration === Infinity || hasInfiniteDuration(this.player)) {
            const url = this._resolveVideoUrl();
            probeInfiniteDuration(url).then((d) => {
              if (d && this.player) {
                this._probedDuration = d;
                this.setState({ duration: d });
              }
            });
            return;
          }
          this._probedDuration = null;
          this.setState({ duration });
        }}
        // YouTube-only stall ENTRY (BUFFERING state) — fast path alongside
        // the rate watch. Release deliberately does NOT come from
        // onBufferEnd: YT fires PLAYING on the first sliver of data, which
        // reopened the gate before real progress resumed (flap < 250ms
        // debounce → the scene never actually froze). _ytWatchTick owns the
        // release once the advance rate recovers. File videos keep their
        // stricter canplaythrough DOM listeners from onReady.
        onBuffer={() => this._reportYoutubeStall(true)}
        onBufferEnd={() => {
          if (isYoutubeUrl(this.props.state.url)) this._setDebugYtState("playing");
        }}
        onReady={this.onReady.bind(this)}
        onError={(err) => {
          // A dead video must not hold the buffer gate closed.
          if (typeof this.props.props.reportVideoStall === "function") {
            try {
              this.props.props.reportVideoStall(this.props.props.id, false);
            } catch (_) {}
          }
          // Load-failure safety: mark ended on error so the scene can complete (visor only)
          if (typeof this.props.props.setVideoEnded === "function") {
            if (this._loadFailureTimer) {
              clearTimeout(this._loadFailureTimer);
              this._loadFailureTimer = null;
            }
            // eslint-disable-next-line no-console
            console.warn(
              "[EnrichedPlayer] video error — marking ended so the scene can complete",
              this.props.state && this.props.state.url,
              err
            );
            this.props.props.setVideoEnded(this.props.props.id);
          }
        }}
      />
    );

    return (
      <div
        ref={(player_wrapper) => {
          this.player_wrapper = player_wrapper;
        }}
        onContextMenu={(e) => {
          e.preventDefault();
        }}
        style={{
          width: "100%",
          height: "100%",
          aspectRatio: this.state.naturalRatio || 16 / 9,
          // In-point guard — never paint the source's t=0 frame for a
          // trimmed/split piece.
          visibility: this.state.atInPoint ? undefined : "hidden",
          ...(geo ? VIDEO_LOOK_WRAPPER_STYLE : null),
        }}
        className="enriched-player-wrapper"
        duration={this.state.duration}
      >
        {geo ? renderVideoLook(React, geo, playerEl) : playerEl}

        {this.state.controls || this.props.props.enabledRightButton ? (
          <div
            className="player-media-controls flexControls visorControls showwithopacity"
            style={{ pointerEvents: "all" }}
          >
            <button className="play-player-button" onClick={this.playPause.bind(this)}>
              {this._playRequested() ? (
                <i className="material-icons">pause</i>
              ) : (
                <i className="material-icons">play_arrow</i>
              )}
            </button>
            <div
              className="progress-player-input dropableRichZone"
              style={{ height: "1.7em", position: "relative" }}
              // value={this.state.played}
              onMouseDown={this.onSeekMouseDown.bind(this)}
              onChange={this.onSeekChange.bind(this)}
              onMouseUp={this.onSeekMouseUp.bind(this)}
            >
              <div className="fakeProgress" style={{ top: "0", zIndex: 0 }} />
              <div
                className="mainSlider"
                style={{
                  position: "absolute",
                  left: this.state.played * 100 + "%",
                  top: "0",
                }}
              />
              {this.state.ready ? markElements : null}
            </div>
            <div className="durationField">
              {fmtTime(this.state.playedSeconds) + "/" + fmtTime(this.state.duration)}
            </div>
            <input
              className="volume-player-input "
              type="range"
              min={0}
              max={1}
              step="any"
              value={this.state.volume}
              onChange={this.setVolume.bind(this)}
            />
            <button
              className="fullscreen-player-button"
              onClick={this.onClickFullscreen.bind(this)}
            >
              {!this.state.fullscreen ? (
                <i className="material-icons">fullscreen</i>
              ) : (
                <i className="material-icons">fullscreen_exit</i>
              )}
            </button>
          </div>
        ) : (
          // Controls hidden: once the video has ended, show only a translucent
          // replay button centred on the video. It stays invisible until the
          // user hovers the player (CSS) and clicking it restarts playback —
          // no persistent control bar (firstEnded is also cleared on play).
          this.state.firstEnded && (
            <div
              className="player-replay-overlay"
              style={{ pointerEvents: "all" }}
              title={i18n.t("EnrichedPlayer.Replay")}
            >
              <button
                className="player-replay-button"
                type="button"
                aria-label="replay"
                onClick={() => this.replayVideo()}
              >
                <i className="material-icons">replay</i>
              </button>
            </div>
          )
        )}
      </div>
    );
    /* <button style={{ background: "#eab700", position: "absolute", top: "50%", left: "50%",fontSize: "50px", padding: "10px", borderRadius: "5px",transform: "translate(-50%, -50%)"}}
                onClick={() => this.setState({playButtonClicked: !this.state.playButtonClicked})}>{i18n.t("EnrichedPlayer.VideoStart")}</button> */
  }
}

/* eslint-enable react/prop-types */
